<template>
  <Teleport to="body" :disabled="!open">
    <sl-dialog :open="open" no-header @sl-request-close="$emit('close')">
      <div class="wishlist-create-dialog">
        <h3 class="wishlist-create-dialog__title">
          {{ $t('wishlist.dialog.createWishlist.title') }}
        </h3>
        <p class="wishlist-create-dialog__subtitle">
          {{ $t('wishlist.dialog.createWishlist.description') }}
        </p>

        <div class="wishlist-create-dialog__status">
          <div class="wishlist-create-dialog__count">
            {{
              $t('wishlist.dialog.currentCount', {
                current: wishlistsList().length,
                max: MAX_WISHLISTS
              })
            }}
          </div>

          <div v-if="numOfAvailableWishlists === 0" class="wishlist-create-dialog__limit-reached">
            {{ $t('wishlist.dialog.maxLimitReached') }}
          </div>
        </div>

        <div v-if="numOfAvailableWishlists > 0" class="wishlist-create-dialog__form">
          <sl-input
            v-model="wishlistName"
            :placeholder="$t('wishlist.dialog.newWishlistName')"
            class="wishlist-create-dialog__name-input"
            :label="$t('wishlist.dialog.wishlistName')"
            required
          />

          <div v-if="errorMessage" class="wishlist-create-dialog__error">
            {{ errorMessage }}
          </div>
        </div>
      </div>

      <sl-button slot="footer" variant="default" @click="$emit('close')">
        {{ $t('globals.cancel') }}
      </sl-button>
      <sl-button
        v-if="numOfAvailableWishlists > 0"
        slot="footer"
        variant="primary"
        :disabled="!wishlistName.trim()"
        :loading="creating"
        @click="createWishlist"
      >
        {{ $t('wishlist.dialog.create') }}
      </sl-button>
    </sl-dialog>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useWishlists } from '@/composables/useWishlists.js'
import { useNotification } from '@/composables/useNotification.js'
import { useCustomerStore } from '@/stores/customer'
import { useDeviceStore } from '@/stores/device'
import { useUiStore } from '@/stores/ui'
import { useI18n } from 'vue-i18n'
import { useFormatDate } from '@/composables/useFormatDate.js'
import '@shoelace-style/shoelace/dist/components/dialog/dialog'
import '@shoelace-style/shoelace/dist/components/button/button'
import '@shoelace-style/shoelace/dist/components/input/input'
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox'
const { wishlistsList, wishlistCreate, wishlistGetFreeWishlistsCount, MAX_WISHLISTS } =
  useWishlists()

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'created'])

const customerStore = useCustomerStore()
const deviceStore = useDeviceStore()
const uiStore = useUiStore()
const notification = useNotification()
const { t } = useI18n()

const wishlistName = ref('')
const creating = ref(false)
const errorMessage = ref('')

const numOfAvailableWishlists = computed(() => {
  return wishlistGetFreeWishlistsCount()
})

// Validate customer and initialize when dialog opens
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      // Check if customer is selected
      if (!customerStore.getCustomerId) {
        emit('close')
        notification.showNotification({
          message: t('wishlist.dialog.customerRequired'),
          type: 'warning'
        })
        uiStore.$patch({
          showCustomerCreationEditDialog: false,
          showCustomerSearchDialog: true
        })
        return
      }

      // Pre-fill wishlist name with default title
      const marketName = JSON.parse(localStorage.getItem('marketSettings'))
      const today = useFormatDate(new Date().toISOString().split('T')[0])
      wishlistName.value = `${marketName.name ? marketName.name :deviceStore.settings.name} ${today}`
    }
    // Reset form when dialog closes
    if (!isOpen) {
      wishlistName.value = ''
      errorMessage.value = ''
    }
  }
)

async function createWishlist() {
  if (!wishlistName.value.trim()) return

  creating.value = true
  errorMessage.value = ''

  try {
    const result = await wishlistCreate(wishlistName.value.trim())

    if (result.success) {
      emit('created', result.wishlist)
      emit('close')
    } else {
      // Handle the error - show localized message
      errorMessage.value = t(result.message)
    }
  } catch (error) {
    console.error('Failed to create wishlist:', error)
    errorMessage.value = t('wishlist.errors.createFailed')
  } finally {
    creating.value = false
  }
}
</script>

<style scoped>
.wishlist-create-dialog {
  padding-top: 10px;

  &__title {
    margin: 0 0 8px 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  &__subtitle {
    margin: 0 0 16px 0;
    color: var(--sl-color-neutral-600);
  }

  &__status {
    margin-bottom: 24px;
    padding: 16px;
    background: var(--sl-color-neutral-50);
    border-radius: var(--sl-border-radius-medium);
    border: 1px solid var(--sl-color-neutral-200);
  }

  &__count {
    font-size: 0.875rem;
    color: var(--sl-color-neutral-700);
    font-weight: 500;
  }

  &__limit-reached {
    margin-top: 8px;
    padding: 12px;
    background: var(--sl-color-warning-50);
    border: 1px solid var(--sl-color-warning-200);
    border-radius: var(--sl-border-radius-small);
    color: var(--sl-color-warning-700);
    font-size: 0.875rem;
    font-weight: 500;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__name-input {
    width: 100%;
  }

  &__color-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__color-label {
    font-weight: 500;
    font-size: 0.875rem;
    color: var(--sl-color-neutral-700);
  }

  &__color-picker {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__options {
    padding-top: 8px;
    border-top: 1px solid var(--sl-color-neutral-200);
  }

  &__error {
    padding: 12px;
    background: var(--sl-color-danger-50);
    border: 1px solid var(--sl-color-danger-200);
    border-radius: var(--sl-border-radius-small);
    color: var(--sl-color-danger-700);
    font-size: 0.875rem;
    font-weight: 500;
    margin-top: 12px;
  }
}

.color-option {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 0 0 1px var(--sl-color-neutral-300);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 0 0 2px var(--sl-color-neutral-400);
  }

  &--selected {
    box-shadow: 0 0 0 3px var(--sl-color-primary-500);
    transform: scale(1.1);

    &::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-weight: bold;
      font-size: 14px;
      text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    }
  }
}
</style>
